import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';

import AboutAdvertStep from './about-advert';
import AboutAddressStep from './about-address';
import AboutPropertyStep from './about-property';
import ButtonPrimary from 'components/common/button/primary';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CreateAdvertFormValues } from 'layout/advert-form/types';
import AboutAnouncerStep from './about-anouncer';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { paths } from 'lib/constants/paths';
import {
    useGetApiV1ClientAdsByIdQuery,
    usePostApiV1ClientAdsMutation,
    usePostApiV1ClientIdentitySendOtpMutation,
    usePutApiV1ClientAdsByIdMutation,
} from 'service/api/allApis';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { isInvalidValue } from 'lib/utils';
import {
    ADVERT_OWNER_TYPE,
    ADVERT_STATUS,
    ADVERT_TYPE,
    BUILDING_TYPE,
    DOCUMENT_STATUS,
    MAINTENANCE_STATUS,
    MORTGAGE_STATUS,
    PRICE_TYPE,
    PROPERTY_TYPE,
    UserType,
} from 'types';
import { LoadingOverlay } from 'components/common/loading-overlay';
import { useSelector } from 'react-redux';
import { selectUser } from 'store/features/userSlice';
import { useModal } from 'hooks/use-modal';

export const getCreateAdvertSchema = (t: TFunction<'translation', undefined>) =>
    z.object({
        propertyArea: z.preprocess(
            (a) => parseFloat(z.any().parse(a)),
            z.number({
                message: t('validation.field-required', { field: t('advert.property-area') }),
            }),
        ),
        propertyLandArea: z.preprocess(
            (a) => parseFloat(z.any().parse(a)),
            z.number({
                message: t('validation.field-required', { field: t('advert.property-land-area') }),
            }),
        ),
        roomCount: z.preprocess(
            (a) => parseInt(z.any().parse(a), 10),
            z
                .number({
                    message: t('validation.field-required', { field: t('advert.room-count') }),
                })
                .int(),
        ),
        buildingFloorCount: z.preprocess(
            (a) => parseInt(z.any().parse(a), 10),
            z
                .number({
                    message: t('validation.field-required', {
                        field: t('advert.building-floor-count'),
                    }),
                })
                .int(),
        ),
        propertyFloor: z.preprocess(
            (a) => parseInt(z.any().parse(a), 10),
            z
                .number({
                    message: t('validation.field-required', { field: t('advert.property-floor') }),
                })
                .int(),
        ),
        price: z.preprocess(
            (a) => parseFloat(z.any().parse(a)),
            z
                .number({
                    message: t('validation.field-required', { field: t('advert.price') }),
                })
                .positive({
                    message: t('validation.positive', { field: t('advert.price'), value: 0 }),
                }),
        ),
        advertOwnerType: z.string(),
        advertType: z.string(),
        propertyType: z.string(),
        maintenanceStatus: z.string(),
        buildingType: z.string(),
        documentStatus: z.string(),
        mortgageStatus: z.string(),
        priceType: z.string(),
        description: z.string().min(10, t('validation.min', { count: 10 })),
        countryId: z
            .string()
            .min(1, t('validation.field-required', { field: t('general.country') })),
        cityId: z.string().min(1, t('validation.field-required', { field: t('general.city') })),
        districtId: z.string(),
        metroId: z.string(),
        name: z.string().min(1, t('validation.field-required', { field: t('auth.name') })),
        surname: z.string().min(1, t('validation.field-required', { field: t('auth.surname') })),
        email: z.string().email(t('validation.invalid-email')),
        phoneNumber: z
            .string()
            .min(10, t('validation.field-required', { field: t('auth.phone-number') })),
        latitude: z.number(),
        longitude: z.number(),
        images: z
            .array(
                z.object({
                    file: z
                        .instanceof(File)
                        .nullable()
                        .optional()
                        .refine((file) => (file?.size ?? 0) < 20 * 1024 * 1024, {
                            message: t('validation.image-size', { size: 20 }),
                        }),
                    id: z.string(),
                    index: z.number(),
                    preview: z.string(),
                    rotate: z.number(),
                }),
            )
            .min(5, t('validation.image-min', { count: 5 })),
        videos: z.array(
            z.object({
                file: z.instanceof(File),
                id: z.string(),
                index: z.number(),
                preview: z.string(),
            }),
        ),
    });

const getdefaultValues = (isAgency: boolean): CreateAdvertFormValues => ({
    advertOwnerType: isAgency ? ADVERT_OWNER_TYPE.AGENCY : ADVERT_OWNER_TYPE.OWNER,
    advertType: ADVERT_TYPE.RENT,
    propertyType: PROPERTY_TYPE.NEW_BUILDING,
    maintenanceStatus: MAINTENANCE_STATUS.FULL,
    documentStatus: DOCUMENT_STATUS.YES,
    mortgageStatus: MORTGAGE_STATUS.YES,
    propertyArea: 0,
    propertyLandArea: 0,
    buildingType: BUILDING_TYPE.BUSINNESS_CENTER,
    roomCount: 0,
    price: 0,
    priceType: PRICE_TYPE.MONTHLY,
    buildingFloorCount: 0,
    propertyFloor: 0,
    images: [],
    videos: [],
    countryId: '',
    cityId: '',
    districtId: '',
    metroId: '',
    targetId: '',
    name: '',
    surname: '',
    email: '',
    phoneNumber: '',
    description: '',
    latitude: 40.409264,
    longitude: 49.867092,
});

type Props = {
    type: 'create' | 'update';
};

const AdvertSteps = ({ type }: Props) => {
    const { user } = useSelector(selectUser);
    const { onOpen } = useModal();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [createMutation, { isLoading: isCreateLoading }] = usePostApiV1ClientAdsMutation();
    const [updateMutation, { isLoading: isUpdateLoading }] = usePutApiV1ClientAdsByIdMutation();
    const [sendOtpMutation, { isLoading: sendOtpLoading }] =
        usePostApiV1ClientIdentitySendOtpMutation();
    const { data, isLoading } = useGetApiV1ClientAdsByIdQuery(
        {
            id: +id!,
        },
        {
            skip: type === 'create',
        },
    );
    const actionLoading = isCreateLoading || isUpdateLoading || sendOtpLoading;

    const form = useForm<CreateAdvertFormValues>({
        resolver: zodResolver(getCreateAdvertSchema(t)),
        defaultValues: getdefaultValues(user?.userType === UserType.AGENCY),
    });

    const onSubmit = (values: CreateAdvertFormValues) => {
        const formData = new FormData();
        const keys: Array<keyof CreateAdvertFormValues> = [
            'advertOwnerType',
            'advertType',
            'propertyType',
            'maintenanceStatus',
            'documentStatus',
            'mortgageStatus',
            'priceType',
            'propertyArea',
            'price',
            'roomCount',
            'buildingType',
            'buildingFloorCount',
            'propertyFloor',
            'description',
            'countryId',
            'cityId',
            'districtId',
            'metroId',
            'targetId',
            'name',
            'surname',
            'email',
            'phoneNumber',
            'propertyLandArea',
            'latitude',
            'longitude',
        ];
        keys.forEach((key) => {
            if (!isInvalidValue(values[key])) formData.append(key, values[key].toString());
        });
        values.images.forEach((image, index) => {
            if (image.file) formData.append(`Images[${index}].image`, image.file);
            else formData.append(`Images[${index}].ExistingImageId`, image.id);
            formData.append(`Images[${index}].index`, image.index.toString());
            formData.append(`Images[${index}].rotate`, image.rotate.toString());
        });
        values.videos.forEach((video, index) => {
            if (video.file) formData.append(`AdsVideos[${index}].video`, video.file);
            else formData.append(`AdsVideos[${index}].ExistingVideoId`, video.id);
            formData.append(`AdsVideos[${index}].index`, video.index.toString());
        });

        if (type === 'create') {
            if (!user) {
                sendOtpMutation({
                    phoneNumber: values.phoneNumber,
                });
                onOpen('advertOtp', {
                    createData: {
                        phoneNumber: values.phoneNumber,
                        formData,
                    },
                });
                return;
            }
            if (user.userType === UserType.USER && user.isFirstAdPost) {
                onOpen('advertPayment', {
                    createData: {
                        formData,
                    },
                });
                return;
            }
            createMutation({
                body: formData as any,
            })
                .unwrap()
                .then(() => {
                    toast.success(t('success.advert-create'));
                    navigate(paths.cabinet.advert);
                })
                .catch((e) => {
                    const errorMessage =
                        e?.data?.Detail ??
                        e?.data?.Title ??
                        e?.data?.message ??
                        t('general.something-went-wrong');
                    toast.error(errorMessage);
                });
        } else {
            updateMutation({
                id: +id!,
                body: formData as any,
            })
                .unwrap()
                .then(() => {
                    toast.success(t('advert.update-pending'));
                    navigate(paths.cabinet.advert);
                })
                .catch((e) => {
                    const errorMessage =
                        e?.data?.Detail ?? e?.data?.Title ?? t('general.something-went-wrong');
                    toast.error(errorMessage);
                });
        }
    };

    useEffect(() => {
        if (!data) return;
        if (!isInvalidValue(data.advertType))
            form.setValue('advertType', data.advertType as ADVERT_TYPE);
        if (!isInvalidValue(data.advertOwnerType))
            form.setValue('advertOwnerType', data.advertOwnerType as ADVERT_OWNER_TYPE);
        if (!isInvalidValue(data.propertyType))
            form.setValue('propertyType', data.propertyType as PROPERTY_TYPE);
        if (!isInvalidValue(data.maintenanceStatus))
            form.setValue('maintenanceStatus', data.maintenanceStatus as MAINTENANCE_STATUS);
        if (!isInvalidValue(data.buildingType))
            form.setValue('buildingType', data.buildingType as BUILDING_TYPE);
        if (!isInvalidValue(data.documentStatus))
            form.setValue('documentStatus', data.documentStatus as DOCUMENT_STATUS);
        if (!isInvalidValue(data.mortgageStatus))
            form.setValue('mortgageStatus', data.mortgageStatus as MORTGAGE_STATUS);
        if (!isInvalidValue(data.priceType))
            form.setValue('priceType', data.priceType as PRICE_TYPE);
        if (!isInvalidValue(data.propertyArea)) form.setValue('propertyArea', data.propertyArea!);
        if (!isInvalidValue(data.price)) form.setValue('price', data.price!);
        if (!isInvalidValue(data.roomCount)) form.setValue('roomCount', data.roomCount!);
        if (!isInvalidValue(data.buildingFloorCount))
            form.setValue('buildingFloorCount', data.buildingFloorCount!);
        if (!isInvalidValue(data.propertyFloor))
            form.setValue('propertyFloor', data.propertyFloor!);
        if (!isInvalidValue(data.description)) form.setValue('description', data.description!);
        if (!isInvalidValue(data.country?.id)) form.setValue('countryId', data.country!.id!);
        if (!isInvalidValue(data.city?.id)) form.setValue('cityId', data.city!.id!);
        if (!isInvalidValue(data.district?.id)) form.setValue('districtId', data.district!.id!);
        if (!isInvalidValue(data.metro?.id)) form.setValue('metroId', data.metro!.id!);
        if (!isInvalidValue(data.target?.id)) form.setValue('targetId', data.target!.id!);
        if (!isInvalidValue(data.name)) form.setValue('name', data.name!);
        if (!isInvalidValue(data.surname)) form.setValue('surname', data.surname!);
        if (!isInvalidValue(data.email)) form.setValue('email', data.email!);
        if (!isInvalidValue(data.phoneNumber)) form.setValue('phoneNumber', data.phoneNumber!);
        if (!isInvalidValue(data.latitude)) form.setValue('latitude', +data.latitude!);
        if (!isInvalidValue(data.longitude)) form.setValue('longitude', +data.longitude!);
        if (data.images) {
            form.setValue(
                'images',
                data.images.map((item) => ({
                    id: item.id!,
                    preview: item.largeImage!,
                    index: item.index!,
                    rotate: item.rotate!,
                    file: null,
                })),
            );
        }
        if (data.adsVideos) {
            form.setValue(
                'videos',
                data.adsVideos.map((item) => ({
                    id: item.id!,
                    preview: item.video!,
                    index: item.index!,
                    file: null,
                })),
            );
        }
    }, [data]);
    useEffect(() => {
        if (form.formState.errors) {
            const errors: string[] = [];
            Object.keys(form.formState.errors).forEach((key) => {
                const error = form.formState.errors[key as keyof typeof form.formState.errors];
                if (Array.isArray(error))
                    error.forEach((e, index) => {
                        if (e?.file?.message) errors.push(`${index + 1}. ${e.file.message}`);
                    });
                else if (error?.message) errors.push(error.message);
            });
            if (errors.length > 0)
                toast.error(
                    <div className="flex flex-col">
                        {errors.map((error, index) => (
                            <p key={index}>{error}</p>
                        ))}
                    </div>,
                );
        }
    }, [form.formState.errors]);

    if (isLoading) {
        return <LoadingOverlay />;
    }

    if (type === 'update' && (!user || !data || data.adsStatus === ADVERT_STATUS.Pending)) {
        return <Navigate to={paths.cabinet.advert} />;
    }

    return (
        <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="border-t-2 border-txt-40 w-full pb-6"
        >
            <AboutAdvertStep form={form} />
            <AboutPropertyStep form={form} />
            <AboutAddressStep form={form} />
            <AboutAnouncerStep form={form} />
            <p className="text-muted-foreground px-4 md:px-6 lg:px-8">
                {t('general.terms-1')}
                <Link to={paths.terms} className="text-brend underline">
                    {t('general.terms-2')}
                </Link>
                {t('general.terms-3')}
            </p>
            <div className="flex justify-end mt-10 px-4 md:px-6 lg:px-8">
                <ButtonPrimary
                    type="submit"
                    loading={actionLoading}
                    text={t('general.continue')}
                    className="w-56 rounded-md uppercase"
                />
            </div>
        </form>
    );
};

export default AdvertSteps;
